import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getInvoicePrintData } from "@/lib/billing-admin.functions";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/billing/$invoiceId/print")({
  component: InvoicePrintPage,
});

const fmt = (cents: number, ccy: string) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(cents / 100);

function InvoicePrintPage() {
  const { invoiceId } = Route.useParams();
  const navigate = useNavigate();
  const fetchFn = useServerFn(getInvoicePrintData);

  const { data } = useQuery({
    queryKey: ["inv-print", invoiceId],
    queryFn: () => fetchFn({ data: { invoiceId } }),
  });
  const invoice = data?.invoice;
  const lines = data?.lines ?? [];
  const customer = data?.customer;
  const tenant = data?.tenant;

  useEffect(() => {
    document.title = invoice ? `Invoice ${invoice.invoice_number}` : "Invoice";
  }, [invoice]);

  const ccy = invoice?.currency ?? "USD";

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="print:hidden border-b bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate({ to: "/app/billing" })}>
            <ArrowLeft className="h-4 w-4 mr-2" />Back to billing
          </Button>
          <Button onClick={() => window.print()}>
            <Printer className="h-4 w-4 mr-2" />Print / Save PDF
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 print:p-0">
        <div className="bg-white text-black p-10 shadow-sm print:shadow-none print:p-0">
          <header className="flex items-start justify-between border-b border-black/20 pb-5">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Invoice</h1>
              <div className="text-sm text-black/60 mt-1">{tenant?.name ?? ""}</div>
            </div>
            <div className="text-right text-sm">
              <div className="text-black/60">Invoice #</div>
              <div className="text-xl font-semibold">{invoice?.invoice_number ?? "—"}</div>
              <div className="text-black/60 mt-2">Status</div>
              <div className="uppercase tracking-wide">{invoice?.status ?? "—"}</div>
            </div>
          </header>

          <section className="grid grid-cols-2 gap-8 my-6 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide text-black/50 mb-1">Bill to</div>
              <div className="font-medium text-base">{customer?.name ?? "—"}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-black/50 mb-1">Period</div>
              <div>{invoice?.period_start ?? "—"} → {invoice?.period_end ?? "—"}</div>
              <div className="text-xs uppercase tracking-wide text-black/50 mt-3 mb-1">Issued</div>
              <div>{invoice?.issued_at ? new Date(invoice.issued_at).toLocaleDateString() : "—"}</div>
            </div>
          </section>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/30 text-left">
                <th className="py-2">Description</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Unit price</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.id} className="border-b border-black/10">
                  <td className="py-2">{l.description}</td>
                  <td className="text-right tabular-nums">{l.qty}</td>
                  <td className="text-right tabular-nums">{fmt(l.unit_price_cents, ccy)}</td>
                  <td className="text-right tabular-nums">{fmt(l.amount_cents, ccy)}</td>
                </tr>
              ))}
              {lines.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-black/50">No lines.</td></tr>
              )}
            </tbody>
          </table>

          <section className="mt-6 flex justify-end">
            <div className="w-72 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-black/60">Subtotal</span><span className="tabular-nums">{fmt(invoice?.subtotal_cents ?? 0, ccy)}</span></div>
              <div className="flex justify-between"><span className="text-black/60">Tax</span><span className="tabular-nums">{fmt(invoice?.tax_cents ?? 0, ccy)}</span></div>
              <div className="flex justify-between border-t border-black/30 pt-2 font-semibold text-base"><span>Total</span><span className="tabular-nums">{fmt(invoice?.total_cents ?? 0, ccy)}</span></div>
            </div>
          </section>

          {invoice?.notes && (
            <section className="mt-8 text-sm">
              <div className="text-xs uppercase tracking-wide text-black/50 mb-1">Notes</div>
              <div className="whitespace-pre-line">{invoice.notes}</div>
            </section>
          )}

          <footer className="mt-10 pt-4 border-t border-black/10 text-xs text-black/50 flex justify-between">
            <span>Thank you for your business.</span>
            <span>Generated {new Date().toLocaleString()}</span>
          </footer>
        </div>
      </div>
    </div>
  );
}